// Chakra imports
import { SimpleGrid, Text, Button, useColorModeValue, Spinner, Center, Alert, AlertIcon, AlertDescription, Box, HStack, Badge, VStack, Progress, Collapse, Link, List, ListItem, ListIcon, Divider, Flex } from "@chakra-ui/react";
import { CheckCircleIcon, WarningIcon, InfoOutlineIcon, ChevronRightIcon, ChevronDownIcon } from "@chakra-ui/icons";
// Custom components
import Card from "components/card/Card.js";
import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import Information from "views/admin/profile/components/Information";

import constants from "providers/Constants";
import useAuthentication from "providers/Authentication";

// Assets
export default function IdentityInformation(props) {

  const authentication = useAuthentication();
  const { t } = useTranslation();
  const [error, setError] = React.useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [document_verification, setDocumentVerification] = useState([]);

  const { user, profile, uploads, countries, ...rest } = props;
  // Chakra Color Mode
  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = "gray.400";
  const cardShadow = useColorModeValue(
    "0px 18px 40px rgba(112, 144, 176, 0.12)",
    "unset"
  );

  useEffect(() => {
    if (profile?.document_upload_country) { getDocumentCategories(profile?.document_upload_country); }
  }, [user, profile, uploads]);

  return (
    <Card mb={{ base: "0px", "2xl": "20px" }} {...rest}>
      {loading ? (
        <Center w='100%' minH={rest?.minH || '220px'}>
          <Spinner size="xl" />
        </Center>
      ) : (
        <>
          <StatusBanner
            status={profile?.document_upload_status}
            message={profile?.document_upload_status_message}
            onAction={submitDocument}
            submittedAt={profile?.document_upload_timestamp}
            checklist={{
              country: !!profile?.document_upload_country,
              type: !!profile?.document_upload_type,
              category: !!profile?.document_upload_category,
              number: !!profile?.document_upload_number,
              front: !!profile?.document_upload_photo_front,
              back: !!profile?.document_upload_photo_back,
              selfie: !!profile?.document_upload_photo_selfie,
            }}
          />
          <SimpleGrid columns='2' gap='20px'>
            <Information
              boxShadow={cardShadow}
              title={t('Document Country')}
              select={true}
              disabled={isLocked(profile?.document_upload_status)}
              select_array={countries}
              value={`${profile?.document_upload_country || t('N/A')}`}
              onChange={e => {
                profile['document_upload_country'] = e;
                getDocumentCategories(e);
                updateProfile('document_upload_country', e);
              }}
            />
            {document_verification.length > 0 && (
              <Information
                boxShadow={cardShadow}
                title={t('Document Type')}
                select={true}
                disabled={isLocked(profile?.document_upload_status)}
                select_array={document_verification}
                hint={t('HINT: (PP - Passport), (DL - Driver License), (ID - Identity Card), (RP - Residence Permit)')}
                value={`${profile?.document_upload_type || t('N/A')}`}
                onChange={e => {
                  profile['document_upload_type'] = e;
                  updateProfile('document_upload_type', e);
                }}
              />
            )}
            {categories.length > 0 && (
              <Information
                boxShadow={cardShadow}
                title={t('Document Category')}
                select={true}
                disabled={isLocked(profile?.document_upload_status)}
                select_array={categories}
                value={`${profile?.document_upload_category || t('N/A')}`}
                onChange={e => {
                  profile['document_upload_category'] = e;
                  updateProfile('document_upload_category', e);
                }}
              />
            )}
            <Information
              boxShadow={cardShadow}
              title={t('Document Number')}
              disabled={isLocked(profile?.document_upload_status)}
              value={`${profile?.document_upload_number || t('N/A')}`}
              onChange={e => {
                profile['document_upload_number'] = e;
                updateProfile('document_upload_number', e);
              }}
            />
            <Information
              boxShadow={cardShadow}
              title={t('Document Upload (Front)')}
              upload={true}
              disabled={isLocked(profile?.document_upload_status)}
              upload_array={uploads}
              value={`${profile?.document_upload_photo_front || t('N/A')}`}
              hint={t('HINT: Select file from your uploads on BlockMap. Use the file uploader tool to upload files')}
              onChange={e => {
                profile['document_upload_photo_front'] = e;
                updateProfile('document_upload_photo_front', e);
              }}
            />
            <Information
              boxShadow={cardShadow}
              title={t('Document Upload (Back)')}
              upload={true}
              disabled={isLocked(profile?.document_upload_status)}
              upload_array={uploads}
              hint={t('HINT: Select file from your uploads on BlockMap. Use the file uploader tool to upload files')}
              value={`${profile?.document_upload_photo_back || t('N/A')}`}
              onChange={e => {
                profile['document_upload_photo_back'] = e;
                updateProfile('document_upload_photo_back', e);
              }}
            />
            <Information
              boxShadow={cardShadow}
              title={t('Selfie Image Upload')}
              upload={true}
              disabled={isLocked(profile?.document_upload_status)}
              upload_array={uploads}
              hint={t('HINT: Select file from your uploads on BlockMap. Use the file uploader tool to upload files')}
              value={`${profile?.document_upload_photo_selfie || t('N/A')}`}
              onChange={e => {
                profile['document_upload_photo_selfie'] = e;
                updateProfile('document_upload_photo_selfie', e);
              }}
            />
            {error.length > 0 &&
              error.map((err, index) => (
                <Text
                  key={index}
                  mb='10px'
                  ms='4px'
                  color={'red'}
                  fontWeight='400'
                  fontSize='md'>
                  * {err.msg}
                </Text>
              ))}
          </SimpleGrid>
        </>
      )}
    </Card>
  );

  // Get document categories
  async function getDocumentCategories(country_iso_code_2) {
    setLoading(true);
    authentication.getVerificationTypes(country_iso_code_2, (error, response) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        const document_verification_map = response?.document_verification?.map(entry => entry.id) || [];
        const categories_map = response?.categories?.map(entry => entry.id) || [];
        setCategories(categories_map);
        setDocumentVerification(document_verification_map);
      }
    });
  }

  // Update profile
  async function updateProfile(key, value) {
    setLoading(true);
    const data = {
      [key]: value,
    }
    authentication.updateUser(data, (error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        constants.showToast(
          "success",
          t('SUCCESS'),
          t('Profile updated successfully')
        );
      }
    });
  }

  // Submit document
  async function submitDocument() {
    const isValid = validateSubmitDocument();
    if (!isValid) return;
    setLoading(true);
    authentication.updateUser({
      action: 'document_verification', identity_document_type: profile?.document_upload_type, identity_document_number: profile?.document_upload_number, identity_document_timestamp: Date.now(), identity_document_verified: false,
      identity_document_status: 'pending', identity_document_country: profile?.document_upload_country, identity_document_status_message: 'Identity document is pending approval!',
      document_upload_type: profile?.document_upload_type, document_upload_category: profile?.document_upload_category,
      document_upload_number: profile?.document_upload_number, document_upload_status: 'pending', document_upload_country: profile?.document_upload_country, document_upload_verified: false,
      document_upload_photo_front: profile?.document_upload_photo_front, document_upload_photo_back: profile?.document_upload_photo_back,
      document_upload_photo_selfie: profile?.document_upload_photo_selfie, document_upload_status_message: 'Your document is pending approval!', document_upload_timestamp: Date.now(),
    }, (error, data) => {
      setLoading(false);
      if (error) {
        setError(error.errors);
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        constants.showToast(
          "success",
          t('SUCCESS'),
          t('Verification document submitted successfully!')
        );
        window.location.reload();
      }
    });
  }

  // Validate submit document
  function validateSubmitDocument() {
    const checks = [
      [!!profile?.document_upload_type, t('Please select document type!')],
      [!!profile?.document_upload_number, t('Please enter document number!')],
      [!!profile?.document_upload_photo_front, t('Please upload document front image!')],
      [!!profile?.document_upload_photo_back, t('Please upload document back image!')],
      [!!profile?.document_upload_photo_selfie, t('Please upload selfie image!')],
    ];
    const errors = checks.filter(([ok]) => !ok).map(([, msg]) => ({ msg }));
    if (errors.length) {
      setError(errors);
      constants.showToast(
        "error",
        t('FAILED'),
        errors.map(e => e.msg).join('\n')
      );
      return false;
    }
    return true;
  }
}

// Status Banner Component
function StatusBanner({ status, message, onAction, submittedAt, checklist }) {
  const { t } = useTranslation();
  const textColorSecondary = "gray.400";
  const s = (status || '').toLowerCase();

  const meta = (() => {
    switch (s) {
      case 'verified':
        return {
          label: t('Verified'),
          chakraStatus: 'success',
          colorScheme: 'green',
          description: message ? t(message) : t('Your identity is verified. No further action required.')
        };
      case 'pending':
        return {
          label: t('Pending'),
          chakraStatus: 'warning',
          colorScheme: 'orange',
          description: message ? t(message) : t('Your documents are under review. This usually takes a moment.')
        };
      case 'rejected':
      case 'failed':
        return {
          label: t('Rejected'),
          chakraStatus: 'error',
          colorScheme: 'red',
          description: message ? t(message) : t('We could not verify your document. Please review and resubmit.'),
          actionLabel: t('Resubmit')
        };
      default:
        return {
          label: t('Not Submitted'),
          chakraStatus: 'info',
          colorScheme: 'blue',
          description: message ? t(message) : t('Upload your ID and selfie to start verification.'),
          actionLabel: t('Submit')
        };
    }
  })();

  const progress = s === 'verified' ? 100 : s === 'pending' ? 66 : s === 'rejected' || s === 'failed' ? 40 : 15;
  const [showTips, setShowTips] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  return (
    <Box mb={4}>
      <Alert status={meta.chakraStatus} variant="left-accent" borderRadius="10px" alignItems="flex-start" py={4} px={4}>
        <AlertIcon />
        <Box flex="1">
          {/* Header row: Title + Status on left, CTA on right */}
          <Flex justify="space-between" align="center" gap={3} w="100%">
            <HStack spacing={3} align="center">
              <Badge colorScheme={meta.colorScheme} borderRadius="full" px={2} py={0.5}>
                {meta.label}
              </Badge>
            </HStack>
            {meta.actionLabel && (
              <Button size="sm" colorScheme={meta.colorScheme} onClick={onAction}>
                {meta.actionLabel} →
              </Button>
            )}
          </Flex>

          {/* Sub-row: message + last submitted on left, Details toggle on right */}
          <Flex justify="space-between" align="center" gap={3} mt={1} w="100%">
            <AlertDescription color={textColorSecondary} fontSize="sm" m={0}>
              {meta.description}
              {constants.formatDate(submittedAt) && (
                <Text as="span" ml={2} fontSize="xs" color={textColorSecondary}>• {t('Last submitted:')} {constants.formatDate(submittedAt)}</Text>
              )}
            </AlertDescription>
            <Button variant="ghost" size="sm" rightIcon={expanded ? <ChevronDownIcon /> : <ChevronRightIcon />} onClick={() => setExpanded(v => !v)}>
              {t('Details')}
            </Button>
          </Flex>

          <Collapse in={expanded} animateOpacity>
            <VStack align="stretch" mt={3} spacing={3}>
              <Progress value={progress} colorScheme={meta.colorScheme} borderRadius="full" size="xs" />
              <List spacing={1} fontSize="sm" color={textColorSecondary}>
                <ChecklistItem ok={checklist?.country} label={t('Document Country')} />
                <ChecklistItem ok={checklist?.type} label={t('Document Type')} />
                <ChecklistItem ok={checklist?.category} label={t('Document Category')} />
                <ChecklistItem ok={checklist?.number} label={t('Document Number')} />
                <ChecklistItem ok={checklist?.front} label={t('Front Image')} />
                <ChecklistItem ok={checklist?.back} label={t('Back Image')} />
                <ChecklistItem ok={checklist?.selfie} label={t('Selfie Image')} />
              </List>
              <Divider />
              <HStack fontSize="sm" color={textColorSecondary} justify="space-between">
                <HStack>
                  <InfoOutlineIcon />
                  <Text>{t('Need help capturing your ID?')}</Text>
                </HStack>
                <Button variant="link" colorScheme={meta.colorScheme} size="sm" onClick={() => setShowTips(v => !v)}>
                  {showTips ? t('Hide tips') : t('Show tips')}
                </Button>
              </HStack>
              <Collapse in={showTips} animateOpacity>
                <Text fontSize="sm" color={textColorSecondary}>
                  {t('Ensure good lighting, avoid glare, and upload clear images of both sides of your document and a selfie with your face visible.')}
                </Text>
              </Collapse>
            </VStack>
          </Collapse>
        </Box>
      </Alert>
    </Box>
  );
}

// Checklist Item Component
function ChecklistItem({ ok, label }) {
  return (
    <ListItem>
      <HStack>
        <ListIcon as={ok ? CheckCircleIcon : WarningIcon} color={ok ? 'green.400' : 'orange.300'} />
        <Text>{label}</Text>
      </HStack>
    </ListItem>
  );
}

// Check if status is locked
function isLocked(status) {
  const s = (status || '').toLowerCase();
  return s === 'verified' || s === 'pending';
}
